import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { TextField, Button } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CreatableSelect from "react-select/creatable";
import { v4 as uuidv4 } from "uuid";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import _ from "lodash";
import { GetError } from "src/constant";
import { addProjectFormValidationSchema } from "src/helpers/yup_schema";
import { getAllNofificationForDashboard } from "src/thunks/dashboard";
import {getAllProjectManagers,addProject,editProject} from "src/thunks/clientProjects";
import toastMessages from "src/constant/toastMessages.json";

export const AddProjectForm = ({setIsAddProjectModalFlag, individualClientInfo, getAllData, editData, setIsEditTextShownForProject, isEditTextShownForProject }) => {

  /** redux store useDispatch & useSelector ------------------------------ **/
  const dispatch = useDispatch();
  const { addProjectLoading } = useSelector((state) => state?.clientProjects);
  const projectManagersList = useSelector((state) => state?.clientProjects?.projectManagersList);

  /** hooks useState ----------------------------------------------------- **/
  const [allProjectDetail, _allProjectDetail] = useState([]);
  const [ProjectDetail, _ProjectDetail] = useState({});
  const [allProjectManagers, _allProjectManagers] = useState([]);
  const [projectManagerError, setProjectManagerError] = useState({
    label: "",
    email: "",
    phoneNumber: "",
  });
  const [projectManagerDropdownValue, setProjectManagerDropdownValue] = useState({});

  /** react hook form declaration ------------------------------ **/
  const {
    register: addProjectRegister,
    handleSubmit: addprojectHandleSubmit,
    formState: { errors: addProjectErrros },
    reset: addProjectReset,
  } = useForm({
    resolver: yupResolver(addProjectFormValidationSchema),
  });

  /** hooks useEffect----------------------------------------------------- **/
  useEffect(() => {
    setEditDataToState();
  }, [editData]);

  useEffect(() => {
    getAllProjectManagersData();
  }, []);

  /** Based on the project manager list, if edit data is present, then remove the project managers that are already added; otherwise, show all project managers in the dropdown -------------------------**/
  useEffect(async () => {
    if (editData) {
      const userIdsToRemove = editData?.users?.map((user) => user.userId);
      const filteredArray = projectManagersList.filter(
        (item) => !userIdsToRemove.includes(item.id)
      );
      _allProjectManagers([...filteredArray]);
    } else {
      _allProjectManagers([...projectManagersList]);
    }
  }, [projectManagersList]);

  useEffect(() => {
    return () => {
      setIsEditTextShownForProject(false);
    };
  }, []);

  /** handler functions--------------------------------------------------- **/

  /** Function used to reset the data in the form, which is required for editing ---------------------------------- **/
  const setEditDataToState = async () => {
    addProjectReset({
      projectName: editData?.name || "",
      intSpacep1: editData?.interiorPrice,
      extSpacep1: editData?.exteriorPrice,
      street: editData?.address?.street1,
      country: editData?.address?.country,
      town: editData?.address?.city,
      postcode: editData?.address?.zip,
    });
    _allProjectDetail(editData?.users || []);
  };

  /** Function to retrieve data for all project managers. ------------------------------------ **/
  const getAllProjectManagersData = async () => {
    try {
      await dispatch(getAllProjectManagers()).unwrap();
    } catch (err) {
      toast.error(GetError(err?.response?.data?.errors) || "something issue");
    }
  };

  /** Function to save the info of a newly added project manager or a selected existing project manager into the state. ------------------------------------- **/
  const handleProjectManagerChange = (selected) => {
    let data = {
      ...selected,
      __isNew__: false,
      email: selected?.value?.email,
      phoneNumber: selected?.value?.phoneNumber,
    };
    let data1 = {
      ...selected,
      id: uuidv4(),
    };
    if (selected.__isNew__ == true) {
      let selectedDataSplit = selected.label.split(" ");
      let newData = {
        ...data1,
        firstName: selectedDataSplit?.length > 0 ? selectedDataSplit[0] : "",
        lastName:
          selectedDataSplit.length > 1 && selectedDataSplit.splice(1).join(" "),
      };
      _ProjectDetail({ ...ProjectDetail, ...newData });
      setProjectManagerDropdownValue({
        ...projectManagerDropdownValue,
        ...newData,
      });
    } else {
      _ProjectDetail({ ...data });
      setProjectManagerDropdownValue({ ...data });
    }
    let errors = { ...projectManagerError };
    if (data?.phoneNumber) {
      errors = { ...errors, phoneNumber: "" };
    }
    if (data?.email) {
      errors = { ...errors, email: "" };
    }
    if (data?.label) {
      errors = { ...errors, label: "" };
    }
    setProjectManagerError({ ...errors });
  };

  /** Function executes when the project manager's email is changed --------------------------------- **/
  const handleProjectManagerEmailChange = (value) => {
    _ProjectDetail({ ...ProjectDetail, email: value });
    setProjectManagerDropdownValue({
      ...projectManagerDropdownValue,
      email: value,
    });
    setProjectManagerError({ ...projectManagerError, email: "" });
  };

  /** function executes when the project manager's phone number is changed ----------------------------------- **/
  const handleProjectManagerPhoneChange = (value) => {
    _ProjectDetail({ ...ProjectDetail, phoneNumber: value });
    setProjectManagerDropdownValue({
      ...projectManagerDropdownValue,
      phoneNumber: value,
    });
    setProjectManagerError({ ...projectManagerError, phoneNumber: "" });
  };

  /** Function to check validation when adding a new project manager ------------------- **/
  const checkValidation = () => {
    let errors = {};
    ["label", "email", "phoneNumber"].map((d) => {
      if (d === "email") {
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        let emailFlag = emailRegex.test(ProjectDetail?.email);
        if (!emailFlag) {
          errors[`${d}`] = "Please add valid email";
        }
      } else if (d == "phoneNumber") {
        const phoneRegex = /^[0-9 -]*$/;
        let phoneFlag = phoneRegex.test(ProjectDetail?.phoneNumber);
        if (!phoneFlag) {
          errors[`${d}`] = "Only numbers allowed";
        }
      } else {
        if (!ProjectDetail[`${d}`]) {
          if (d == "label") {
            errors[`${d}`] = "Please add project manager name here";
          } else {
            errors[`${d}`] = `Please add ${d} here`;
          }
        } else {
          delete errors[`${d}`];
        }
      }
    });
    return errors;
  };

  /** Function to handle the click event for adding a new project manager. -------------------------- **/
  const handleAddProjectManagerClick = (data) => {
    let errors = checkValidation();
    if (Object.keys(errors).length > 0) {
      setProjectManagerError({
        ...projectManagerError,
        ...errors,
      });
    } else {
      setProjectManagerError({
        ...projectManagerError,
        email: "",
        label: "",
        phoneNumber: "",
      });
    }

    if (Object.keys(errors).length == 0) {
      let newProject = { id: uuidv4(), ...ProjectDetail };
      _allProjectDetail([...allProjectDetail, { ...newProject }]);
      _allProjectManagers(
        allProjectManagers.filter(
          (option) => option.id !== projectManagerDropdownValue?.value?.id
        )
      );
      _ProjectDetail({});
    }
  };

  /** Function to clear the fields entered by the user for adding a project manager ------------------------- **/
  const handleClearProjectClick = () => {
    _ProjectDetail({});
    setProjectManagerError({
      ...projectManagerError,
      email: "",
      phoneNumber: "",
      label: "",
    });
  };

  /** Function to remove project managers that have already been added -------------------------- **/
  const handlerRemoveData = (item) => {
    let newData = _.filter(allProjectDetail, (d) => d.id !== item?.id);
    _allProjectDetail([...newData]);
    if (item?.__isNew__ == false) {
      _allProjectManagers([...allProjectManagers, { ...item?.value }]);
    } else {
      _allProjectManagers([...allProjectManagers, { ...item }]);
    }
  };

  /** Function to handle the click event of the cancel button. ------------------ **/
  const handleCancelButtonClick = () => {
    setIsAddProjectModalFlag(false);
    addProjectReset();
  };

  /** Function for adding a new project or editing an existing one. ---------------------- **/
  const onAddProjectSubmitHandler = async (data) => {
    let grantAppAcess = _.find(allProjectDetail, (d) => d?.grantAppAccess);

    let apiData = {
      clientId: individualClientInfo?.id,
      clientName: individualClientInfo?.name,
      interiorPrice: parseFloat(`${data?.intSpacep1}`),
      address: {
        street1: data.street,
        country: data.country,
        city: data.town,
        zip: data.postcode,
      },
      exteriorPrice: parseFloat(`${data?.extSpacep1}`),

      name: data?.projectName,
      details: "",
      contact: "",
      users: _.map(allProjectDetail, (d) => {
        return {
          email: d?.email || "",
          firstName: d?.value?.firstName || d?.value || "",
          middleName: d?.value?.middleName || "",
          phoneNumber: d?.phoneNumber || "",
          lastName: d?.value?.lastName || "",
          grantAppAccess: grantAppAcess?.grantAppAccess || false,
        };
      }),
    };
    try {
      const isedit = editData?.id || false;
      if (isedit) {
        apiData = {
          ...apiData,
          id: editData?.id,
          isParentResourceUpdated: true,
        };
      }
      let result = await dispatch(
        isedit
          ? editProject({ data: apiData, id: editData?.id || "" })
          : addProject({ ...apiData })
      ).unwrap();
      getAllData();
      if (result) {
        handleCancelButtonClick();
        if (isedit) {
          toast.success(toastMessages.projectEdited);
        } else {
          toast.success(toastMessages.projectAdded);
        }
      }
      setTimeout(() => {
        dispatch(getAllNofificationForDashboard(1, 20));
      }, 2000);
    } catch (err) {
      toast.error(GetError(err?.response?.data?.errors) || "something issue");
    }
  };

  return (
    <>
      <div>
        <div className="depot-form-header-style">
          <span className="allocate-mgnr-header-color me-2">
            {isEditTextShownForProject ? "EDIT PROJECT" : "ADD PROJECT"}
          </span>
          <span className="allocate-mgnr-subheader-color">
            {individualClientInfo?.name || ""}
          </span>
        </div>
        <form onSubmit={addprojectHandleSubmit(onAddProjectSubmitHandler)}>
          <div style={{ paddingLeft: 100, paddingRight: 100, marginTop: 30 }}>
            <div className="row" style={{ height: 65 }}>
              <div className="col-md-8 add-depot-form-mainclass">
                <TextField
                  className="depot-form-textfields-style"
                  {...addProjectRegister("projectName")}
                  id="standard-basic"
                  label="PROJECT NAME"
                  variant="standard"
                />
                <div className="error-field-class">
                  {addProjectErrros?.projectName?.message &&
                    addProjectErrros?.projectName?.message}
                </div>
              </div>
              <div className="col-md-4"></div>
            </div>

            <span className="section-name-form-style">RATES</span>

            <div className="row rates-row-style">
              <div className="col-md-4 d-flex align-items-center">
                <span className="section-name-form-style me-1">INTERNAL</span>
                <span className="section-name-form-style me-1">&#163;</span>
                <input
                  {...addProjectRegister("intSpacep1")}
                  className={`rate-input-styles-for-add-project ${addProjectErrros?.intSpacep1?.message
                      ? "add-border-for-rates-input"
                      : ""
                    }`}
                  type="number"
                  step="any"
                ></input>
              </div>
              <div className="col-md-4 d-flex align-items-center">
                <span className="section-name-form-style me-1">EXTERNAL</span>
                <span className="section-name-form-style me-1">&#163;</span>
                <input
                  {...addProjectRegister("extSpacep1")}
                  className={`rate-input-styles-for-add-project ${addProjectErrros.extSpacep1?.message
                      ? "add-border-for-rates-input"
                      : ""
                    }`}
                  type="number"
                  step="any"
                ></input>
              </div>

              <div className="col-md-4"></div>
            </div>
            <span className="section-name-form-style">ADDRESS</span>
            <div className="row m-0">
              <TextField
                className="depot-form-textfields-style"
                {...addProjectRegister("street")}
                id="standard-basic"
                label="STREET"
                variant="standard"
              />
              <div className="error-field-class depotform-street-class">
                {addProjectErrros.street?.message &&
                  addProjectErrros.street?.message}
              </div>
            </div>
            <div className="row">
              <div className="col-md-4">
                <TextField
                  className="depot-form-textfields-style"
                  {...addProjectRegister("town")}
                  id="standard-basic"
                  label="TOWN"
                  variant="standard"
                />
                <div className="error-field-class">
                  {addProjectErrros.town?.message &&
                    addProjectErrros.town?.message}
                </div>
              </div>

              <div className="col-md-4">
                <TextField
                  className="depot-form-textfields-style"
                  {...addProjectRegister("country")}
                  id="standard-basic"
                  label="COUNTRY"
                  variant="standard"
                />
                <div className="error-field-class">
                  {addProjectErrros.country?.message &&
                    addProjectErrros.country?.message}
                </div>
              </div>
              <div className="col-md-4">
                <TextField
                  className="depot-form-textfields-style"
                  {...addProjectRegister("postcode")}
                  id="standard-basic"
                  label="POSTCODE"
                  variant="standard"
                />
                <div className="error-field-class">
                  {addProjectErrros.postcode?.message &&
                    addProjectErrros.postcode?.message}
                </div>
              </div>
            </div>

            <span className="section-name-form-style mt-3">
              PROJECT MANAGER (S)
            </span>
            <div className="table-responsive main-div-admin-project-tab d-flex flex-column mt-2">
              <table
                className="table admintab-depots-personal-table table-borderless"
                style={{ width: "100%" }}
              >
                <tbody className="depotsperosnal-admin-tbl-body">
                  {allProjectDetail.map((item, index) => {
                    return (
                      <React.Fragment key={index}>
                        <tr>
                          <td>
                            {item?.value?.firstName ||
                              item?.value ||
                              item?.firstName}{" "}
                          </td>
                          <td>{item?.email}</td>
                          <td>{item?.phoneNumber}</td>
                          <td>
                            <div
                              style={{
                                color: "red",
                                fontSize: 12,
                                cursor: "pointer",
                              }}
                              onClick={() => handlerRemoveData(item)}
                            >
                              <RemoveCircleOutlineOutlinedIcon
                                className="remove-item-icon-color me-1"
                                style={{ cursor: "pointer" }}
                                fontSize="20"
                              />
                              REMOVE
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="row" style={{ height: 60 }}>
              <CreatableSelect
                isClearable
                value={{ label: ProjectDetail?.label || "" }}
                options={allProjectManagers?.map((item, index) => {
                  return {
                    label: item.firstName + " " + item.lastName,
                    value: item,
                    key: index,
                  };
                })}
                onChange={handleProjectManagerChange}
              />
              <div className="error-field-class">
                {projectManagerError?.label && projectManagerError?.label}
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 add-depot-form-mainclass">
                <TextField
                  disabled={ProjectDetail.__isNew__ == false ? true : false}
                  className="depot-form-textfields-style"
                  id="standard-basic"
                  value={ProjectDetail?.email || ""}
                  onChange={({ target }) =>
                    handleProjectManagerEmailChange(target.value)
                  }
                  label="EMAIL"
                  variant="standard"
                />
                <div className="error-field-class">
                  {projectManagerError?.email && projectManagerError?.email}
                </div>
              </div>
              <div className="col-md-4 add-depot-form-mainclass">
                <TextField
                  disabled={ProjectDetail.__isNew__ == false ? true : false}
                  className="depot-form-textfields-style"
                  id="standard-basic"
                  value={ProjectDetail?.phoneNumber || ""}
                  label="PHONE"
                  type="string"
                  variant="standard"
                  onChange={({ target }) =>
                    handleProjectManagerPhoneChange(target.value)
                  }
                />
                <div className="error-field-class">
                  {projectManagerError?.phoneNumber &&
                    projectManagerError?.phoneNumber}
                </div>
              </div>
              <div className="col-md-4 d-flex justify-content-end mt-3">
                <Button
                  variant="outlined"
                  className="btn-bootstrap-submit-style"
                  style={{ color: "red", borderColor: "red", height: 35 }}
                  startIcon={<CancelIcon />}
                  onClick={() => handleClearProjectClick()}
                >
                  CLEAR
                </Button>
                <Button
                  style={{ height: 35 }}
                  variant="outlined"
                  className="btn-bootstrap-submit-style ms-3"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleAddProjectManagerClick()}
                >
                  ADD
                </Button>
              </div>
            </div>

            <div
              className="d-flex flex-row justify-content-between"
              style={{ marginTop: 120, marginBottom: 20 }}
            >
              <Button
                variant="outlined"
                className="btn-bootstrap-submit-style"
                style={{ color: "red", borderColor: "red" }}
                startIcon={<CancelIcon />}
                onClick={() => handleCancelButtonClick()}
              >
                Cancel
              </Button>
              {isEditTextShownForProject ? (
                <Button
                  disabled={addProjectLoading ? true : false}
                  startIcon={<CheckCircleIcon />}
                  type="submit"
                  className={`btn-bootstrap-submit-style ${addProjectLoading ? "fade-out" : ""
                    }`}
                >
                  Save
                </Button>
              ) : (
                <Button
                  disabled={addProjectLoading ? true : false}
                  startIcon={<CheckCircleIcon />}
                  type="submit"
                  className={`btn-bootstrap-submit-style ${addProjectLoading ? "fade-out" : ""
                    }`}
                >
                  Submit
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );
};
